import type { TrailDefinition, TrailContext } from './types'

interface MagicParticle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  size: number
  color: [number, number, number]
  twinklePhase: number
  twinkleSpeed: number
}

const MAX_PARTICLES = 60

const MAGIC_COLORS: [number, number, number][] = [
  [180, 120, 255], // purple
  [120, 200, 255], // cyan
  [255, 160, 220], // pink
  [160, 255, 200], // mint
  [255, 220, 120] // gold
]

export const magic: TrailDefinition = {
  id: 'magic',
  name: 'Magic',

  init() {
    return [] as MagicParticle[]
  },

  update(state: unknown, ctx: TrailContext) {
    const particles = state as MagicParticle[]
    const dt = ctx.deltaTime / 1000

    if (ctx.mouseActive && dt > 0) {
      const spawnCount = Math.min(3, MAX_PARTICLES - particles.length)
      for (let i = 0; i < spawnCount; i++) {
        const angle = Math.random() * Math.PI * 2
        const speed = 15 + Math.random() * 45
        const color = MAGIC_COLORS[Math.floor(Math.random() * MAGIC_COLORS.length)]
        particles.push({
          x: ctx.mouseX + (Math.random() - 0.5) * 10,
          y: ctx.mouseY + (Math.random() - 0.5) * 10,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 10,
          life: 1,
          maxLife: 0.5 + Math.random() * 0.4,
          size: 1.5 + Math.random() * 2,
          color,
          twinklePhase: Math.random() * Math.PI * 2,
          twinkleSpeed: 6 + Math.random() * 8
        })
      }
    }

    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i]
      p.life -= dt / p.maxLife
      p.x += p.vx * dt
      p.y += p.vy * dt
      p.vx *= 0.96
      p.vy *= 0.96

      if (p.life <= 0) {
        particles.splice(i, 1)
      }
    }
  },

  draw(state: unknown, ctx: TrailContext) {
    const particles = state as MagicParticle[]
    const c = ctx.ctx
    const t = ctx.time / 1000

    for (const p of particles) {
      const alpha = Math.max(0, p.life)
      const twinkle = (Math.sin(t * p.twinkleSpeed + p.twinklePhase) + 1) / 2
      const finalAlpha = alpha * (0.5 + twinkle * 0.5)
      const [r, g, b] = p.color
      const size = p.size * (0.5 + alpha * 0.5)

      // Star shape
      c.save()
      c.translate(p.x, p.y)
      c.globalAlpha = finalAlpha

      c.beginPath()
      const points = 4
      const outerR = size
      const innerR = size * 0.35
      for (let i = 0; i < points * 2; i++) {
        const angle = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2
        const radius = i % 2 === 0 ? outerR : innerR
        c.lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius)
      }
      c.closePath()
      c.fillStyle = `rgb(${r}, ${g}, ${b})`
      c.shadowColor = `rgba(${r}, ${g}, ${b}, 0.6)`
      c.shadowBlur = size * 4
      c.fill()

      c.restore()
    }
    c.globalAlpha = 1
  }
}
